
import React, { useState } from 'react';

import Slide from '@mui/material/Slide';
import { Box, styled } from "@mui/system";

import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';


import TextField from '@mui/material/TextField';

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from '@mui/material/IconButton';
import { BasicExportForm } from './ui/BasicExportForm';
import { IFeatureDescription, IFile } from 'library/interface';
import { HeadCell } from './ui/EnhancedTable';
import { Height } from '@mui/icons-material';

import useAxios from 'library/axios';

import entityService from 'features/services/Entity';

import { currentUserSessionAtom, isExportBoxShowAtom, currentBasicTextFilterPropsAtom } from 'library/store';
import { FormDialog } from './ui/FormDialog';
import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { setConstantValue } from 'typescript';

import { globalConfig } from "config";


export interface DownloadFormDialogProps {
  open: boolean,
  onClose: () => void,

  entityId: number,
  entityName: string,
}



interface UploadStatus {
  uploadedChunks: number[];
}

const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB
const PARALLEL_LIMIT = 5; // number of concurrent uploads


export function DownloadFormDialog(props: DownloadFormDialogProps) {
  const { t } = useTranslation();
  const axios = useAxios();
  const { userId, token } = useRecoilValue(currentUserSessionAtom);

  const { getBackupFiles } = entityService();

  const { open, onClose, entityId, entityName } = props;

  const [fileType, setFileType] = useState('');
  const [reportName, setReportName] = useState('');

  const [backupFiles, setBackupFiles] = useState<IFile[]>([]);
  const [backupFileName, setBackupFileName] = useState('');

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);


  // 🔹 Resolve file information dynamically
  const getFileConfig = () => {
    switch (fileType) {
      case 'backend':
        return { folder: 'uploaded-app', fileName: 'KampuxAPI.zip', downloadName: 'KampuxAPI.zip' };
      case 'frontend':
        return { folder: 'uploaded-app', fileName: 'KampuxWEB.zip', downloadName: 'KampuxWEB.zip' };
      case 'report':
        return {
          folder: 'uploaded-reports',
          fileName: reportName ? `${reportName}.zip` : 'Report.zip',
          downloadName: reportName ? `${reportName}.zip` : 'Report.zip',
        };
      case 'backup':
        return {
          folder: 'db-backup', fileName: backupFileName, downloadName: backupFileName,
        };
      default:
        throw new Error('Please select a file type.');
    }
  };


  const downloadFile = async () => {
    // const { folder, fileName } = getFileConfig();
    // window.open(`${globalConfig.get().apiUrl}/api/home/download?folder=${folder}&fileName=${fileName}`, '_blank');

    setIsDownloading(true);
    setProgress(0);
    setStatus(`${t('Initializing download')}...`);

    const { folder, fileName, downloadName } = getFileConfig();

    try {
      // 1️⃣ Get file size from backend metadata
      const metaRes = await axios.get(`/api/home/download-metadata?folder=${folder}&fileName=${fileName}`);
      const totalSize = metaRes.data.fileSize;
      const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

      let completed = 0;
      const chunkIndexes = Array.from({ length: totalChunks }, (_, i) => i);
      const chunks: Uint8Array[] = new Array(totalChunks);

      setStatus(`Downloading ${totalChunks} chunks...`);

      // 2️⃣ Download queue for parallel workers
      const downloadQueue = async () => {
        while (chunkIndexes.length > 0) {
          const index = chunkIndexes.shift();
          if (index === undefined) return;

          const start = index * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE - 1, totalSize - 1);

          try {
            const res = await axios.get(
              `/api/home/download?folder=${folder}&fileName=${fileName}`,
              {
                headers: { Range: `bytes=${start}-${end}` },
                responseType: 'arraybuffer',
              }
            );

            chunks[index] = new Uint8Array(res.data);
            completed++;
            setProgress(Math.round((completed / totalChunks) * 100));
          } catch (err) {
            console.warn(`Chunk ${index} failed, retrying...`, err);
            chunkIndexes.push(index); // retry failed chunk
          }
        }
      };

      // 3️⃣ Start parallel workers
      const workers = Array.from({ length: PARALLEL_LIMIT }, downloadQueue);
      await Promise.all(workers);

      setStatus('Merging chunks...');

      // 4️⃣ Merge and trigger browser download
      //const fullBlob = new Blob(chunks.filter(Boolean) as Uint8Array[], { type: 'application/octet-stream' });

      const fullBlob = new Blob(
        chunks.filter(Boolean).map(c => new Uint8Array(c!)), // force type to Uint8Array
        { type: 'application/octet-stream' }
      );

      const url = window.URL.createObjectURL(fullBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      window.URL.revokeObjectURL(url);

      setStatus(`✅ ${t('Download complete')}!`);
    } catch (error) {
      console.error(error);
      setStatus(t('Download failed'));
    } finally {
      setIsDownloading(false);
    }

  };

  return (
    <FormDialog open={open} maxWidth='sm' height='60vh'
      okText={''} cancelText='' title={`${t('Download')} ...`} onCancel={onClose}
      onClose={onClose} onOk={onClose}  >
      <Stack flexDirection='column'  >
        <Box sx={{ mt: 1, width: '100%' }} >
          <TextField select  value={fileType} 
            onChange={async (e) => { // this is not the best way to do it, but we just do it like that...
                  setFileType(e.target.value);
                  if(e.target.value === 'backup') {
                    const arr = await getBackupFiles();
                    setBackupFiles(arr);
                  }
                }
              }
            // sx={{ width: 'calc(100% - 8px)' }} 
            fullWidth
            id="fileType"
            label={t('Select file type')} >
            <MenuItem value="backend">{'Backend (kampux-api.zip)'}</MenuItem>
            <MenuItem value="frontend">{'Frontend (kampux-web.zip)'}</MenuItem>
            <MenuItem value="report">{'Report (report.zip)'}</MenuItem>
            { globalConfig.get().superUsers.includes( `-${userId}-`)  &&
                <MenuItem value="backup">{'Backup (xxxx.bak*)'}</MenuItem>}
          </TextField>
        </Box>

        {fileType === 'report' && <Box sx={{ mt: 1, width: '100%' }} >
          {/* 🔹 Report name input */}
          <TextField
            sx={{ width: 'calc(100% - 8px)' }}
            //fullWidth
            label={t('Report name')}
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
          //disabled={isDownloading}
          />
        </Box>}
        {fileType === 'backup' && <Box sx={{ mt: 1, width: '100%' }} >
          <TextField select onChange={(e) => setBackupFileName(e.target.value)} value={backupFileName}
            // sx={{ width: 'calc(100% - 8px)' }} 
            fullWidth
            id="backupFileName"
            inputProps={ { style: {textTransform: 'none'} } }
            label={t('Backup to download')} >
            {backupFiles.map( x => <MenuItem value={x.fileName}>{x.fileName}</MenuItem>)}
          </TextField>
        </Box>}
        <Box sx={{ mt: 4, width: '100%' }} >

          <Button fullWidth variant="contained" disabled={isDownloading} onClick={downloadFile}>
            {isDownloading ? `${t('Downloading')}...` : t('Download File')}
          </Button>
        </Box>
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <progress value={progress} max="100" style={{ width: '90%' }} />
          <div>{progress}%</div>
          <p>{status}</p>
        </Box>
      </Stack>
    </FormDialog>
  );
}
